import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { Upload, Loader2, CheckCircle, XCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function TikTokTest() {
  const [videoFile, setVideoFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (50MB max)
      if (file.size > 50 * 1024 * 1024) {
        toast.error("Video file too large. Maximum size is 50MB.");
        return;
      }
      // Validate file type
      if (!file.type.startsWith("video/")) {
        toast.error("Please select a video file.");
        return;
      }
      setVideoFile(file);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!videoFile) {
      toast.error("Please select a video file first.");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Please log in first.");
      return;
    }

    setUploading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("video", videoFile);
      formData.append("caption", caption || "Test video from YASNO Marketing");

      const response = await fetch(`${API}/api/tiktok/upload-and-post`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();
      setResult(data);

      if (data.success) {
        toast.success("Video posted to TikTok successfully!");
      } else {
        toast.error(data.error || "Failed to post video");
      }
    } catch (error) {
      console.error("Upload error:", error);
      setResult({ success: false, error: error.message });
      toast.error("Network error. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-8">
      <div className="max-w-2xl mx-auto">
        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader>
            <CardTitle className="text-2xl text-white flex items-center gap-2">
              🎵 TikTok Video Upload Test
            </CardTitle>
            <p className="text-gray-400">
              Upload a video directly from your computer to test TikTok posting.
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Video File Input */}
            <div className="space-y-2">
              <label className="text-sm text-gray-300">Video File (MP4, MOV, WebM - Max 50MB)</label>
              <Input
                type="file"
                accept="video/*"
                onChange={handleFileChange}
                className="bg-gray-700 border-gray-600 text-white"
                data-testid="video-file-input"
              />
              {videoFile && (
                <p className="text-sm text-green-400">
                  ✓ Selected: {videoFile.name} ({(videoFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
            </div>

            {/* Caption Input */}
            <div className="space-y-2">
              <label className="text-sm text-gray-300">Caption (optional)</label>
              <Textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Enter a caption for your TikTok video..."
                className="bg-gray-700 border-gray-600 text-white"
                rows={3}
                data-testid="caption-input"
              />
            </div>

            {/* Upload Button */}
            <Button
              onClick={handleUpload}
              disabled={!videoFile || uploading}
              className="w-full bg-pink-600 hover:bg-pink-700"
              data-testid="upload-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading to TikTok...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload & Post to TikTok
                </>
              )}
            </Button>

            {/* Result Display */}
            {result && (
              <div
                className={`p-4 rounded-lg ${
                  result.success
                    ? "bg-green-900/50 border border-green-700"
                    : "bg-red-900/50 border border-red-700"
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  {result.success ? (
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-400" />
                  )}
                  <span className={result.success ? "text-green-400" : "text-red-400"}>
                    {result.success ? "Success!" : "Failed"}
                  </span>
                </div>
                {result.success ? (
                  <div className="text-sm text-gray-300">
                    <p>Post ID: {result.post_id}</p>
                    <p>{result.post_url}</p>
                  </div>
                ) : (
                  <p className="text-sm text-red-300">{result.error}</p>
                )}
              </div>
            )}

            {/* Instructions */}
            <div className="text-sm text-gray-400 space-y-2 pt-4 border-t border-gray-700">
              <p><strong>Requirements:</strong></p>
              <ul className="list-disc list-inside space-y-1">
                <li>Video must be MP4, MOV, or WebM format</li>
                <li>Maximum file size: 50MB</li>
                <li>TikTok must be connected from Dashboard</li>
                <li>Video will be posted as "Only Me" (private) for testing</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Back to Dashboard */}
        <div className="mt-4 text-center">
          <a href="/dashboard" className="text-blue-400 hover:text-blue-300">
            ← Back to Dashboard
          </a>
        </div>
      </div>
    </div>
  );
}
